import { Button, cn } from '@teach/ui'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { isSelectable, packagePrice, type CreditPackage } from './lib/paymentMethods'
import { useMessages, useUiLang } from '@/i18n'
import { messages } from './messages'

interface Props {
  pkg: CreditPackage
  /** После покупки этого пакета хватит на упавший прогон. */
  fits: boolean
  /** Есть ли почта в профиле: без неё рублёвой цены и способа не существует. */
  canPayByCard: boolean
  /** Страна профиля: от неё зависит валюта цены (PRD #71, п.28). */
  country: string | null
  onSelect: () => void
}

/**
 * Плитка пакета: без рамок, заливка и крупное скругление. Заголовок — название
 * пакета; номинал под ним печатается, только если отличается от названия (у
 * пакета «200 кредитов» второй такой же строки не будет); цена ОДНА, в валюте,
 * которую предложил бэк (`packagePrice`). Пакет без единого способа оплаты не
 * печатает ни цены, ни живой кнопки — иначе «$0.00» и «Выбрать» вели бы в шаг
 * «этот пакет сейчас нельзя оплатить».
 */
export const PackageCard = ({ pkg, fits, canPayByCard, country, onSelect }: Props) => {
  const m = useMessages(messages)
  const price = packagePrice(pkg, country)
  const payable = isSelectable(pkg, canPayByCard, country)
  const credits = formatCreditsWithWord(pkg.credits)
  // Имя пакета бэк отдаёт только по-русски («200 кредитов», вопрос Пете п.24) —
  // в английском интерфейсе заголовком стоит номинал на языке интерфейса.
  const title = useUiLang() === 'ru' ? pkg.name : credits
  // На телефоне — строка (название и цена слева, кнопка справа): столбики во всю
  // ширину не помещали в шторку и трёх пакетов. С `sm` — прежняя плитка.
  return (
    <div
      data-package-card=""
      className={cn(
        'relative flex items-center gap-3 rounded-2xl p-4 transition-colors sm:flex-col sm:items-stretch sm:justify-between sm:gap-6 sm:p-5',
        fits ? 'bg-brand/15' : 'bg-background/60',
      )}
    >
      {fits ? (
        <span className="absolute -top-2.5 right-4 rounded-full bg-brand px-3 py-0.5 text-xs font-semibold text-brand-foreground">
          {m.fitsRun}
        </span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-6">
        <div className="space-y-0.5 sm:space-y-1">
          <h3 className="text-base font-semibold sm:text-xl">{title}</h3>
          {title.trim().toLowerCase() !== credits.toLowerCase() ? (
            <p className="text-xs text-muted-foreground sm:text-sm">{credits}</p>
          ) : null}
        </div>
        {price ? (
          <p className="text-xl font-bold sm:text-3xl">{price}</p>
        ) : (
          <p className="text-sm text-muted-foreground">{m.packagePaymentUnavailable}</p>
        )}
      </div>
      <Button
        variant={fits ? 'default' : 'secondary'}
        className="shrink-0 sm:w-full"
        disabled={!payable}
        onClick={onSelect}
      >
        {m.choose}
      </Button>
    </div>
  )
}
